import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CreditCard, Calendar, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Subscription } from '@/types';

interface CustomerDetails {
  id: string;
  name: string;
  subscription?: Partial<Subscription>;
}

export function CustomerSubscriptionPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState<CustomerDetails | null>(null);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [selectedCycle, setSelectedCycle] = useState<'monthly' | 'yearly'>('monthly');

  useEffect(() => {
    checkAdminAccess();
    fetchCustomerDetails();
  }, [customerId]);

  const checkAdminAccess = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/login');
      return;
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .single();

    if (profile?.role !== 'admin') {
      navigate('/admin/customers');
      toast.error('Unauthorized access');
    }
  };

  const fetchCustomerDetails = async () => {
    try {
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select(`
          id,
          name,
          subscriptions (
            id,
            plan,
            status,
            features,
            price,
            billing_cycle,
            start_date,
            end_date
          )
        `)
        .eq('id', customerId)
        .single();

      if (companyError) throw companyError;

      setCustomer({
        id: company.id,
        name: company.name,
        subscription: company.subscriptions?.[0],
      });

      if (company.subscriptions?.[0]) {
        setSelectedPlan(company.subscriptions[0].plan);
        setSelectedCycle(company.subscriptions[0].billing_cycle);
      }
    } catch (error) {
      console.error('Error fetching customer details:', error);
      toast.error('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubscription = async () => {
    try {
      setLoading(true);

      const features = getFeaturesByPlan(selectedPlan);
      const price = getPriceByPlan(selectedPlan, selectedCycle);
      const startDate = new Date().toISOString();
      const endDate = selectedCycle === 'yearly' 
        ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
        : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      if (customer?.subscription?.id) {
        // Update existing subscription
        const { error } = await supabase
          .from('subscriptions')
          .update({
            plan: selectedPlan,
            status: 'active',
            features,
            price,
            billing_cycle: selectedCycle,
            start_date: startDate,
            end_date: endDate,
          })
          .eq('id', customer.subscription.id);

        if (error) throw error;
      } else {
        // Create new subscription
        const { error } = await supabase
          .from('subscriptions')
          .insert([{
            company_id: customer?.id,
            plan: selectedPlan,
            status: 'active',
            features,
            price,
            billing_cycle: selectedCycle,
            start_date: startDate,
            end_date: endDate,
          }]);

        if (error) throw error;
      }

      toast.success('Subscription updated successfully');
      fetchCustomerDetails();
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error('Failed to update subscription');
    } finally {
      setLoading(false);
    }
  };

  const getFeaturesByPlan = (plan: string): string[] => {
    switch (plan) {
      case 'enterprise':
        return [
          'Unlimited job postings',
          'Custom SSO integration',
          'GitHub SSO integration',
          'API access',
          'Priority support',
          'Custom branding',
          'Advanced analytics',
          'Bulk imports',
          'Custom workflows'
        ];
      case 'pro':
        return [
          'Up to 50 job postings',
          'Basic SSO integration',
          'GitHub SSO integration',
          'API access',
          'Email support',
          'Basic analytics',
          'Bulk imports'
        ];
      case 'basic':
        return [
          'Up to 10 job postings',
          'GitHub SSO integration',
          'Email support',
          'Basic analytics'
        ];
      default:
        return [];
    }
  };

  const getPriceByPlan = (plan: string, cycle: 'monthly' | 'yearly'): number => {
    const monthlyPrices: Record<string, number> = {
      enterprise: 999,
      pro: 299,
      basic: 99,
    };

    const price = monthlyPrices[plan] || 0;
    return cycle === 'yearly' ? price * 10 : price; // 2 months free for yearly
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">{customer?.name}</h1>
          <p className="text-muted-foreground">Manage customer subscription</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/admin/customers')}>
          Back to Customers
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Current Subscription</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {customer?.subscription ? (
              <>
                <div className="flex justify-between items-center">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Plan</p>
                    <Badge variant="secondary" className="text-lg">
                      {customer.subscription.plan?.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="text-right space-y-1">
                    <p className="text-sm font-medium">Status</p>
                    <Badge variant={customer.subscription.status === 'active' ? 'default' : 'destructive'}>
                      {customer.subscription.status?.toUpperCase()}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <Calendar className="h-4 w-4 mr-2" />
                    Start Date: {new Date(customer.subscription.start_date || '').toLocaleDateString()}
                  </div>
                  <div className="flex items-center text-sm">
                    <Calendar className="h-4 w-4 mr-2" />
                    End Date: {new Date(customer.subscription.end_date || '').toLocaleDateString()}
                  </div>
                  <div className="flex items-center text-sm">
                    <CreditCard className="h-4 w-4 mr-2" />
                    ${customer.subscription.price}/
                    {customer.subscription.billing_cycle === 'monthly' ? 'month' : 'year'}
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Features</p>
                  <ul className="space-y-2">
                    {customer.subscription.features?.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm">
                        <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            ) : (
              <div className="text-center py-6">
                <XCircle className="h-12 w-12 mx-auto text-muted-foreground" />
                <p className="mt-2 text-muted-foreground">No active subscription</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Update Subscription</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Plan</label>
              <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Billing Cycle</label>
              <Select value={selectedCycle} onValueChange={(value: 'monthly' | 'yearly') => setSelectedCycle(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select billing cycle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly (2 months free)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectedPlan && (
              <div className="space-y-4 mt-6">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium">Price</span>
                  <span className="text-2xl font-bold">
                    ${getPriceByPlan(selectedPlan, selectedCycle)}/
                    {selectedCycle === 'monthly' ? 'month' : 'year'}
                  </span>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium">Included Features</p>
                  <ul className="space-y-2">
                    {getFeaturesByPlan(selectedPlan).map((feature, index) => (
                      <li key={index} className="flex items-center text-sm">
                        <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <Button 
                  className="w-full" 
                  onClick={handleUpdateSubscription}
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Update Subscription'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}